// Using the live Vercel URL
const API_URL = 'https://smartbrain-theta.vercel.app/api/ai';

export const generateQuizQuestions = async ({ text = '', base64Pdf = null, numQuestions = 5 }) => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'generate_quiz',
        text,
        base64Pdf,
        numQuestions
      })
    });

    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const errorData = await response.json();
        console.error('AI API Error Data:', errorData);
        if (errorData?.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // failed to parse json
      }
      throw new Error(`API error: ${errorMessage}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating questions with backend AI API:', error);
    throw error;
  }
};

export const chatWithTeacherAssistant = async (message, chatHistory = []) => {
  try {
    let combinedText = '';
    if (chatHistory && chatHistory.length > 0) {
      combinedText = 'Previous Conversation History:\n';
      chatHistory.forEach(msg => {
        combinedText += `${msg.role === 'user' ? 'Teacher' : 'Assistant'}: ${msg.text}\n`;
      });
      combinedText += '\nNew Request from Teacher:\n';
    }
    combinedText += message;

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'teacher_assistant',
        text: combinedText
      })
    });

    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const errorData = await response.json();
        if (errorData?.error) errorMessage = errorData.error;
      } catch (e) {}
      throw new Error(`API error: ${errorMessage}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in teacher assistant chat:', error);
    throw error;
  }
};

/**
 * Intelligent Document & Curriculum Synthesizer
 * Parses a syllabus, scheme of work, or chapter notes (PDF / Text)
 * and extracts structured topics, lesson notes, and review questions.
 */
export const parseCurriculumDocument = async ({ text = '', base64Pdf = null, classLevel = '', subject = '', term = '' }) => {
  try {
    const hintText = [
      classLevel ? `Target Class Level: ${classLevel}` : '',
      subject ? `Target Subject: ${subject}` : '',
      term ? `Target Academic Term: ${term}` : '',
    ].filter(Boolean).join('\n');

    const combinedText = hintText ? `${hintText}\n\nDocument Content:\n${text}` : text;

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'parse_curriculum_document',
        text: combinedText,
        base64Pdf,
      }),
    });

    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const errorData = await response.json();
        if (errorData?.error) errorMessage = errorData.error;
      } catch (e) {}
      throw new Error(`API error: ${errorMessage}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error parsing curriculum document:', error);
    throw error;
  }
};

/**
 * Intelligent Multi-Lesson Topic Splitter
 * Analyzes a document specifically for one topic and extracts all distinct lessons/subtopics.
 */
export const splitTopicLessons = async ({
  text = '',
  base64Pdf = null,
  topicTitle = '',
  classLevel = '',
  subject = '',
  term = ''
}) => {
  try {
    const hintText = [
      topicTitle ? `Target Topic: ${topicTitle}` : '',
      classLevel ? `Target Class Level: ${classLevel}` : '',
      subject ? `Target Subject: ${subject}` : '',
      term ? `Target Academic Term: ${term}` : '',
    ].filter(Boolean).join('\n');

    const combinedText = hintText ? `${hintText}\n\nDocument Content:\n${text}` : text;

    // 1. Try action: 'split_topic_lessons' on live serverless API
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'split_topic_lessons',
          topicTitle,
          classLevel,
          subject,
          term,
          text: combinedText,
          base64Pdf,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data && !data.error) {
          const rawLessons = data.lessons || (Array.isArray(data) ? data : data.topics) || [];
          if (rawLessons.length > 0) {
            return { lessons: rawLessons };
          }
        }
      }
    } catch (_) {}

    // 2. Fallback to live deployed action: 'parse_curriculum_document' (already active on Vercel)
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'parse_curriculum_document',
          text: combinedText,
          base64Pdf,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data && !data.error) {
          const items = data.topics || (Array.isArray(data) ? data : data.lessons) || [];
          if (items.length > 0) {
            const lessons = items.map((t, idx) => ({
              lessonNumber: t.lessonNumber || t.week || idx + 1,
              title: t.title || `Lesson ${idx + 1}`,
              subtopic: (t.subtopics && t.subtopics[0]) || t.subtopic || 'Core Concept',
              learningObjectives: t.learningObjectives || [],
              lessonNotes: t.lessonNotes || '',
              analogies: t.analogies || '',
              keyRule: t.keyRule || '',
              practiceQuestions: t.practiceQuestions || [],
            }));
            return { lessons };
          }
        }
      }
    } catch (_) {}

    // 3. Fallback: Direct Gemini REST call using VITE_GEMINI_API_KEY (zero backend dependency)
    const geminiKey = import.meta.env.VITE_GEMINI_API_KEY || 'AQ.Ab8RN6IEDcA76VMJZkofVJNe8skWdFiv7IDZtya2PQfmbJ0Qmg';
    if (geminiKey) {
      try {
        const candidateModels = [
          'gemini-2.5-flash',
          'gemini-2.0-flash',
          'gemini-1.5-flash',
          'gemini-flash-latest'
        ];

        for (const model of candidateModels) {
          try {
            const geminiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${geminiKey}`;
            const prompt = `You are an expert Nigerian secondary school curriculum synthesizer and teacher.
Analyze the provided document for the specific topic: "${topicTitle}".
Extract EVERY SINGLE separate sub-lesson or concept under this topic.
For each lesson return:
- lessonNumber: integer (1, 2, 3...)
- title: specific lesson title
- subtopic: concept strand
- learningObjectives: list of strings
- lessonNotes: thorough explanation with definitions and worked examples
- analogies: everyday analogy
- keyRule: governing formula or rule
- practiceQuestions: array of questions with question, options (4 options), correctOptionIndex (0-3), and explanation.

Return strictly a valid JSON object matching this schema:
{
  "lessons": [
    {
      "lessonNumber": 1,
      "title": "Clear Lesson Title",
      "subtopic": "Specific Concept Strand",
      "learningObjectives": ["Objective 1", "Objective 2"],
      "lessonNotes": "Thorough lesson text...",
      "analogies": "Analogy...",
      "keyRule": "Formula...",
      "practiceQuestions": [
        {
          "question": "Question text?",
          "options": ["A", "B", "C", "D"],
          "correctOptionIndex": 0,
          "explanation": "Explanation..."
        }
      ]
    }
  ]
}

Document Content:
${combinedText.slice(0, 50000)}`;

            const geminiRes = await fetch(geminiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                contents: [{ parts: [{ text: prompt }] }],
                generationConfig: {
                  responseMimeType: 'application/json',
                  temperature: 0.2,
                },
              }),
            });

            if (geminiRes.ok) {
              const geminiData = await geminiRes.json();
              const rawJsonText = geminiData.candidates?.[0]?.content?.parts?.[0]?.text;
              if (rawJsonText) {
                const parsed = JSON.parse(rawJsonText);
                const rawLessons = parsed.lessons || (Array.isArray(parsed) ? parsed : parsed.topics) || [];
                if (rawLessons.length > 0) {
                  return { lessons: rawLessons };
                }
              }
            }
          } catch (_) {}
        }
      } catch (_) {}
    }

    throw new Error('Could not connect to AI services. Please check your internet connection or paste lecture notes directly.');
  } catch (error) {
    console.error('Error in splitTopicLessons:', error);
    throw error;
  }
};

/**
 * Convert a File object to base64 string
 */
export const fileToBase64 = (fileObj) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(fileObj);
    reader.onload = () => {
      const result = reader.result;
      const base64 = typeof result === 'string' && result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = reject;
  });
};

/**
 * Direct In-Browser PDF Text Extractor (Zero Server Dependency, 100% Client-side)
 * Uses PDF.js to extract all pages, text, and structure from a PDF file or Blob.
 */
export const extractTextFromPdfInBrowser = async (fileOrBlob) => {
  try {
    if (!window.pdfjsLib) {
      await new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[src*="pdf.min.js"]');
        if (existingScript) {
          if (window.pdfjsLib) return resolve();
          existingScript.addEventListener('load', resolve);
          existingScript.addEventListener('error', reject);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          if (window.pdfjsLib) {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc =
              'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            resolve();
          } else {
            reject(new Error('pdfjsLib not loaded properly'));
          }
        };
        script.onerror = () => reject(new Error('Failed to load PDF.js from CDN'));
        document.head.appendChild(script);
      });
    }

    let arrayBuffer;
    if (typeof fileOrBlob === 'string') {
      const resp = await fetch(fileOrBlob);
      if (!resp.ok) throw new Error(`Failed to download PDF (${resp.status} ${resp.statusText})`);
      arrayBuffer = await resp.arrayBuffer();
    } else {
      arrayBuffer = await fileOrBlob.arrayBuffer();
    }

    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(' ');
      if (pageText.trim()) {
        fullText += `[Page ${i}]\n${pageText}\n\n`;
      }
    }
    const cleanText = fullText.trim();
    return {
      success: true,
      text: cleanText,
      pageCount: pdf.numPages,
      wordCount: cleanText ? cleanText.split(/\s+/).filter(Boolean).length : 0,
    };
  } catch (err) {
    console.warn('In-browser PDF extraction error, falling back to server:', err);
    throw err;
  }
};

/**
 * In-Browser Word (.docx, .doc) Text Extractor
 * Uses CDN-loaded Mammoth for .docx files, and a robust binary text scanner fallback for .doc/raw files.
 */
export const extractTextFromWordInBrowser = async (fileOrBlob) => {
  try {
    const arrayBuffer = await fileOrBlob.arrayBuffer();

    // 1. Try Mammoth from CDN (industry standard for .docx extraction)
    try {
      if (!window.mammoth) {
        await new Promise((resolve, reject) => {
          const existingScript = document.querySelector('script[src*="mammoth"]');
          if (existingScript) {
            if (window.mammoth) return resolve();
            existingScript.addEventListener('load', resolve);
            existingScript.addEventListener('error', reject);
            return;
          }
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js';
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load mammoth from CDN'));
          document.head.appendChild(script);
        });
      }

      if (window.mammoth) {
        const result = await window.mammoth.extractRawText({ arrayBuffer });
        const cleanText = (result?.value || '').trim();
        if (cleanText.length > 20) {
          return {
            success: true,
            text: cleanText,
            wordCount: cleanText.split(/\s+/).filter(Boolean).length,
          };
        }
      }
    } catch (mammothErr) {
      console.warn('Mammoth extraction failed, falling back to binary scan:', mammothErr);
    }

    // 2. Binary / XML fallback for .docx / .doc files
    const uint8 = new Uint8Array(arrayBuffer);
    let decoder = new TextDecoder('utf-8', { fatal: false });
    let rawStr = decoder.decode(uint8);

    // If it's a docx XML stream, extract text inside <w:t> tags
    if (rawStr.includes('<w:t')) {
      const matches = rawStr.match(/<w:t[^>]*>([^<]+)<\/w:t>/g) || [];
      if (matches.length > 0) {
        const extracted = matches
          .map((m) => m.replace(/<[^>]+>/g, ''))
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();
        if (extracted.length > 30) {
          return {
            success: true,
            text: extracted,
            wordCount: extracted.split(/\s+/).filter(Boolean).length,
          };
        }
      }
    }

    // 3. Fallback: extract printable strings of length >= 4 (works for .doc binary format)
    const printableWords = [];
    let curWord = '';
    for (let i = 0; i < uint8.length; i++) {
      const byte = uint8[i];
      // ASCII printable or newline
      if ((byte >= 32 && byte <= 126) || byte === 10 || byte === 13) {
        curWord += String.fromCharCode(byte);
      } else {
        if (curWord.trim().length >= 4 && /[a-zA-Z]/.test(curWord)) {
          printableWords.push(curWord.trim());
        }
        curWord = '';
      }
    }
    if (curWord.trim().length >= 4) {
      printableWords.push(curWord.trim());
    }

    const filteredText = printableWords.join(' ').replace(/\s{2,}/g, ' ').trim();
    if (filteredText.length > 50) {
      return {
        success: true,
        text: filteredText,
        wordCount: filteredText.split(/\s+/).filter(Boolean).length,
      };
    }

    throw new Error('Could not extract readable text from this Word document. Please try saving as PDF or pasting notes directly.');
  } catch (err) {
    console.error('Word extraction error:', err);
    throw err;
  }
};

/**
 * Intelligent Document Text Extractor
 * Extracts full text from PDF, Word (.docx, .doc), or Text documents.
 */
export const extractDocumentText = async ({ text = '', base64Pdf = null, file = null, pdfUrl = null }) => {
  // 1. Try instant in-browser extraction first if file is provided
  if (file) {
    const fileName = (file.name || '').toLowerCase();
    const isWord = fileName.endsWith('.docx') || fileName.endsWith('.doc') || file.type.includes('word') || file.type.includes('officedocument');
    if (isWord) {
      return await extractTextFromWordInBrowser(file);
    }
    const isPdf = fileName.endsWith('.pdf') || file.type === 'application/pdf';
    if (isPdf) {
      try {
        const browserResult = await extractTextFromPdfInBrowser(file);
        if (browserResult && browserResult.text) return browserResult;
      } catch (e) {
        console.warn('Browser extraction failed, using server fallback...', e);
      }
    }
  } else if (pdfUrl) {
    try {
      const browserResult = await extractTextFromPdfInBrowser(pdfUrl);
      if (browserResult && browserResult.text) return browserResult;
    } catch (e) {
      console.warn('Browser extraction failed, using server fallback...', e);
    }
  }

  // 2. Server candidate fallback
  const candidateUrls = [
    API_URL,
    'http://localhost:3000/api/ai',
    'http://localhost:3001/api/ai',
  ];

  let lastError = null;
  for (const url of candidateUrls) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'extract_text',
          text,
          base64Pdf,
          pdfUrl,
        }),
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error('Failed to extract text from document.');
};

/**
 * Intelligent Text Chunking for RAG & AI Retrieval
 * Splits long lesson notes into structured semantic segments with metadata.
 */
export const chunkLessonContent = (rawText, chunkSize = 1000, overlap = 150) => {
  if (!rawText || typeof rawText !== 'string') return [];
  const text = rawText.replace(/\r\n/g, '\n').trim();
  if (!text) return [];

  // If text is short, return as a single primary chunk
  if (text.length <= chunkSize) {
    return [
      {
        chunkIndex: 0,
        text,
        characterCount: text.length,
        wordCount: text.split(/\s+/).filter(Boolean).length,
      },
    ];
  }

  const chunks = [];
  let startIndex = 0;
  let chunkIdx = 0;

  while (startIndex < text.length) {
    let endIndex = startIndex + chunkSize;

    // Avoid cutting words or sentences in the middle if possible
    if (endIndex < text.length) {
      const nextBreak = text.indexOf('\n\n', endIndex - 100);
      if (nextBreak !== -1 && nextBreak <= endIndex + 100) {
        endIndex = nextBreak;
      } else {
        const nextPeriod = text.indexOf('. ', endIndex - 50);
        if (nextPeriod !== -1 && nextPeriod <= endIndex + 50) {
          endIndex = nextPeriod + 1;
        }
      }
    } else {
      endIndex = text.length;
    }

    const chunkStr = text.substring(startIndex, endIndex).trim();
    if (chunkStr.length > 0) {
      chunks.push({
        chunkIndex: chunkIdx++,
        text: chunkStr,
        characterCount: chunkStr.length,
        wordCount: chunkStr.split(/\s+/).filter(Boolean).length,
      });
    }

    if (endIndex >= text.length) break;
    startIndex = Math.max(startIndex + 1, endIndex - overlap);
  }

  return chunks;
};

/**
 * Intelligent RAG Vector Chunker & Dense Embedder
 * Calls the backend RAG pipeline to semantically segment text or PDF and generate 3072-dimensional vector embeddings.
 */
export const chunkAndEmbedLessonContent = async ({ text = '', base64Pdf = null, pdfUrl = null, chunkSize = 900, overlap = 150 }) => {
  const candidateUrls = [
    API_URL,
    'http://localhost:3000/api/ai',
    'http://localhost:3001/api/ai',
  ];

  let lastError = null;
  for (const url of candidateUrls) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'chunk_and_embed',
          text,
          base64Pdf,
          pdfUrl,
          chunkSize,
          overlap,
        }),
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      lastError = err;
    }
  }

  // Fallback to local semantic chunking if network/embedding fails
  const localChunks = chunkLessonContent(text, chunkSize, overlap);
  return {
    success: true,
    totalChunks: localChunks.length,
    vectorModel: 'client-offline-chunker',
    vectorDimensions: 0,
    chunks: localChunks,
  };
};

/**
 * RAG Vector Search over Pre-Chunked Lesson Notes
 * Computes cosine similarity between query and stored chunk embeddings.
 */
export const ragSearchLessonNotes = async ({ query, chunks = [], maxResults = 5 }) => {
  const candidateUrls = [
    API_URL,
    'http://localhost:3000/api/ai',
    'http://localhost:3001/api/ai',
  ];

  for (const url of candidateUrls) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'rag_search',
          query,
          chunks,
          maxResults,
        }),
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (_) {}
  }

  // Fallback: simple lexical search if server is unreachable
  const qTokens = (query || '').toLowerCase().split(/\s+/).filter(Boolean);
  const scored = chunks.map((c) => {
    const text = typeof c === 'string' ? c : (c.text || '');
    let matches = 0;
    qTokens.forEach((t) => {
      if (text.toLowerCase().includes(t)) matches += 1;
    });
    return {
      chunkIndex: c.chunkIndex ?? 0,
      pageNumber: c.pageNumber ?? null,
      text,
      similarityScore: matches / Math.max(1, qTokens.length),
    };
  });
  scored.sort((a, b) => b.similarityScore - a.similarityScore);
  return {
    success: true,
    query,
    totalCandidates: chunks.length,
    results: scored.slice(0, maxResults),
  };
};

